import { create } from 'zustand';
import { useAuthStore } from '@/store/auth';
import { getStepsForFlavor, type TourFlavor, type TourStep } from '@/components/tour/steps';

/**
 * EVII guided tour — ephemeral UI state.
 *
 * Not persisted: the tour itself is a transient overlay. What persists is the
 * COMPLETION, via the server flag (`tour_completed_at`, POST /me/tour-complete)
 * mirrored into the auth store — so once the driver finishes OR exits, the
 * first-launch auto-open never fires again, on this device or another.
 *
 * Flow: open() shows the driver-type chooser (greet) → start(flavor) loads that
 * flavor's steps and runs them → next/back walk the steps → exit()/finish()
 * close and persist completion.
 */

type TourPhase = 'greet' | 'running';

interface TourState {
  active: boolean;
  phase: TourPhase;
  flavor: TourFlavor | null;
  steps: TourStep[];
  stepIndex: number;
  /** Open at the driver-type chooser (greet). */
  open: () => void;
  /** Pick a flavor and start the guided steps. */
  start: (flavor: TourFlavor) => void;
  next: () => void;
  back: () => void;
  /** Pick a branch (the multifamily fork): append the chosen tail after the
   *  current fork step and advance into it. Back returns to the fork. */
  branch: (tail: TourStep[]) => void;
  /** Dismiss the whole tour. Persists completion so it never auto-opens again. */
  exit: () => void;
  /** Reached the end. Persists completion. */
  finish: () => void;
}

const CLOSED = {
  active: false,
  phase: 'greet' as TourPhase,
  flavor: null,
  steps: [] as TourStep[],
  stepIndex: 0,
};

/** Mark the tour complete server-side (idempotent) + sync the auth store so the
 *  auto-open never re-fires. Dynamic import breaks the api.ts <-> store cycle,
 *  mirroring auth store refreshUser. Soft-fails to a local mark. */
async function persistTourComplete(): Promise<void> {
  const stamp = new Date();
  try {
    const { completeTour } = await import('@/lib/api');
    const { tour_completed_at } = await completeTour();
    useAuthStore.getState().updateUser({
      tour_completed_at: tour_completed_at ? new Date(tour_completed_at) : stamp,
    });
  } catch {
    // Network hiccup: still mark locally so the tour doesn't nag again this
    // session. The next successful /auth/me reconciles the canonical value.
    useAuthStore.getState().updateUser({ tour_completed_at: stamp });
  }
}

export const useTourStore = create<TourState>((set, get) => ({
  ...CLOSED,
  open: () => set({ ...CLOSED, active: true, phase: 'greet' }),
  start: (flavor) =>
    set({ active: true, phase: 'running', flavor, steps: getStepsForFlavor(flavor), stepIndex: 0 }),
  next: () => {
    const { stepIndex, steps } = get();
    if (stepIndex >= steps.length - 1) {
      get().finish();
      return;
    }
    set({ stepIndex: stepIndex + 1 });
  },
  back: () => set((s) => ({ stepIndex: Math.max(0, s.stepIndex - 1) })),
  branch: (tail) =>
    set((s) => ({
      steps: [...s.steps.slice(0, s.stepIndex + 1), ...tail],
      stepIndex: s.stepIndex + 1,
    })),
  exit: () => {
    set({ ...CLOSED });
    void persistTourComplete();
  },
  finish: () => {
    set({ ...CLOSED });
    void persistTourComplete();
  },
}));
